import { Node } from '@tiptap/core';
import DOMPurify from 'dompurify';

const SVG_ATTRIBUTES = ['viewBox', 'width', 'height', 'preserveAspectRatio', 'class', 'style'];

const safeSvgContent = (content: string): string => {
  const safe = DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg">${content}</svg>`, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['foreignObject', 'script', 'animate', 'animateMotion', 'animateTransform', 'set'],
    RETURN_DOM: true
  });
  return safe instanceof Element ? (safe.querySelector('svg')?.innerHTML ?? '') : '';
};

const applySvgAttributes = (el: SVGElement, attrs: Record<string, unknown>) => {
  for (const name of SVG_ATTRIBUTES) {
    const value = attrs[name];
    if (value) el.setAttribute(name, String(value));
    else el.removeAttribute(name);
  }
};

export const SvgNode = Node.create({
  name: 'svg',

  group: 'inline',
  atom: true,
  selectable: true,
  inline: true,

  // A chart is styled and sized from the document's own stylesheet, so dropping class or
  // style here silently unstyles every diagram the moment the document is edited.
  addAttributes() {
    return {
      svgContent: { default: '' },
      viewBox: { default: null },
      width: { default: null },
      height: { default: null },
      preserveAspectRatio: { default: null },
      class: { default: null },
      style: { default: null }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'svg',
        getAttrs: (element) => {
          const el = element as unknown as SVGSVGElement;
          return {
            svgContent: safeSvgContent(el.innerHTML || ''),
            viewBox: el.getAttribute('viewBox'),
            width: el.getAttribute('width'),
            height: el.getAttribute('height'),
            preserveAspectRatio: el.getAttribute('preserveAspectRatio'),
            class: el.getAttribute('class'),
            style: el.getAttribute('style')
          };
        }
      }
    ];
  },

  renderHTML({ node }) {
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    applySvgAttributes(el, node.attrs);
    el.innerHTML = safeSvgContent(node.attrs.svgContent ?? '');
    return el;
  },

  addNodeView() {
    return ({ node }) => {
      const wrapper = document.createElement('span');
      wrapper.style.display = 'inline-block';
      wrapper.style.verticalAlign = 'middle';
      wrapper.style.lineHeight = '0';
      const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      el.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      applySvgAttributes(el, node.attrs);
      el.innerHTML = safeSvgContent(node.attrs.svgContent ?? '');
      el.setAttribute('contenteditable', 'false');
      wrapper.appendChild(el);
      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type !== this.type) return false;
          el.innerHTML = safeSvgContent(updatedNode.attrs.svgContent ?? '');
          applySvgAttributes(el, updatedNode.attrs);
          return true;
        }
      };
    };
  }
});
